import { useState, useEffect } from 'react';
import queryString from 'query-string';
import { fetchArModelService } from '../viewSettingsService';

const MODEL_VIEWER_CDN = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.4.0/model-viewer.min.js';

const loadModelViewer = () => new Promise((resolve) => {
    if (customElements.get('model-viewer')) return resolve();
    const script = document.createElement('script');
    script.type = 'module';
    script.src = MODEL_VIEWER_CDN;
    script.onload = resolve;
    document.head.appendChild(script);
});

/**
 * Resolves the AR model URL for the current artwork + frame.
 *
 * The GLB is now built and cached server-side (POST /wallpreview/ar-model) and
 * served from the media CDN. This replaces the old in-browser build that
 * produced a `blob:` URL — blob URLs only work for model-viewer's WebXR mode,
 * so Android Scene Viewer and iOS Quick Look (separate apps that fetch the
 * model over HTTP) could never load them. A real CDN URL works in every mode.
 *
 * clientId / artworkId come from the URL query string, matching how ARViewer
 * loads the 2D view settings.
 */
const useARModel = (frameType = 'None') => {
    const [objectUrl, setObjectUrl] = useState(null);
    const [status, setStatus]       = useState('Loading…');
    const [ready, setReady]         = useState(false);
    const [error, setError]         = useState(null);

    useEffect(() => {
        const { clientId, artworkId } = queryString.parse(window.location.search);
        if (!clientId || !artworkId) return;

        let cancelled = false;
        setReady(false);
        setError(null);
        setStatus('Preparing 3D model…');

        const load = async () => {
            try {
                // Ensure the <model-viewer> custom element is defined, and ask
                // the API for this frame's hosted GLB — in parallel.
                const [, arModel] = await Promise.all([
                    loadModelViewer(),
                    fetchArModelService({ clientId, artworkId, frameType }),
                ]);
                if (cancelled) return;

                if (!arModel || !arModel.url) {
                    throw new Error('AR model unavailable for this artwork');
                }

                setObjectUrl(arModel.url);
                setStatus('Drag to rotate · Scroll to zoom');
                setReady(true);
            } catch (e) {
                if (cancelled) return;
                setError({ message: 'Could not prepare the 3D model. Please try again.' });
                setStatus(null);
                console.error('[ARViewer]', e);
            }
        };

        load();

        return () => { cancelled = true; };
    }, [frameType]);

    return { objectUrl, status, ready, error };
};

export default useARModel;
